import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiCamera, FiCheck, FiClock, FiMonitor, FiRefreshCw, FiSlash, FiUser, FiVideo, FiVolumeX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import styles from './CandidateWelcome.module.css';

const ROLE_FULL = {
  RSM: 'Regional Sales Manager',
  FSM: 'Field Sales Manager',
  TSE: 'Territory Sales Manager'
};

const SECTIONS = [
  { num: 1, title: 'Cognitive & Analytical Skills', desc: 'Logical reasoning, numerical ability, and verbal reasoning', count: '50 questions' },
  { num: 2, title: 'Personality & Emotional Intelligence', desc: 'Behavioral tendencies and self-awareness', count: '50 questions (Likert scale)' },
  { num: 3, title: 'Leadership & Team Management', desc: 'Scenario-based leadership judgment', count: '20 questions' },
  { num: 4, title: 'Technical Case Study', desc: 'Sales performance analysis and problem-solving', count: '5 analytical questions' },
];

export default function CandidateWelcome() {
  const { candidate } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showProctoringWarning, setShowProctoringWarning] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [requireCamera, setRequireCamera] = useState(true);
  const firstName = candidate?.fullName?.split(' ')[0] || 'Candidate';
  const roleLabel = ROLE_FULL[candidate?.role] || candidate?.role;

  // Prevent browser back button from working
  useEffect(() => {
    // Push a dummy state to the history stack
    window.history.pushState(null, null, window.location.href);

    const handlePopState = () => {
      // Push again to prevent actual back navigation
      window.history.pushState(null, null, window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Set camera requirement from candidate object
  useEffect(() => {
    setRequireCamera(candidate?.requireCamera !== false); // Default to true if not set
  }, [candidate?.requireCamera]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showModal || showProctoringWarning) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showProctoringWarning]);

  const handleStart = () => {
    setShowModal(true);
  };

  const handleRequestCamera = async () => {
    setCameraLoading(true);
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      
      // Verify camera is actually working by checking for video track
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        stream.getTracks().forEach(track => track.stop());
        setCameraError('Camera access denied. Please enable camera permissions to proceed.');
        setCameraLoading(false);
        return;
      }

      // Create a temporary video element to verify camera is producing frames
      const tempVideo = document.createElement('video');
      tempVideo.srcObject = stream;
      tempVideo.play();

      // Wait a bit for camera to initialize
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if camera is actually showing video (not covered/dark)
      const canvas = document.createElement('canvas');
      canvas.width = tempVideo.videoWidth;
      canvas.height = tempVideo.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(tempVideo, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let brightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      brightness = brightness / (data.length / 4);

      // If brightness is very low (< 30), camera is likely covered
      if (brightness < 30) {
        stream.getTracks().forEach(track => track.stop());
        setCameraError('Camera appears to be covered or very dark. Please uncover your camera.');
        setCameraLoading(false);
        return;
      }

      // Camera is working - stop the temporary stream
      stream.getTracks().forEach(track => track.stop());
      setCameraEnabled(true);
    } catch (err) {
      setCameraError('Camera access denied. Please enable camera permissions to proceed.');
      console.error('Camera access error:', err);
    } finally {
      setCameraLoading(false);
    }
  };

  const handleConfirmStart = () => {
    if (requireCamera && !cameraEnabled) return;
    setShowModal(false);
    setShowProctoringWarning(true);
  };

  const handleProctoringConfirm = () => {
    setShowProctoringWarning(false);
    navigate('/assessment', { replace: true });
  };

  const handleProctoringCancel = () => {
    setShowProctoringWarning(false);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setCameraEnabled(false);
    setCameraError(null);
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.topBar}>
          <div className={styles.logoRow}>
            <img src="/logo.png" alt="UAC Foods Logo" className={styles.logoMark} />
            <span className={styles.logoText}>UAC Foods — Sales Leadership Assessment</span>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.greetBlock}>
            <h2 className={styles.greet}>Welcome, {firstName}</h2>
            <span className={`${styles.roleTag} ${styles[`role_${candidate?.role}`]}`}>{roleLabel}</span>
          </div>

          <p className={styles.intro}>
            This assessment evaluates your capabilities across four dimensions critical for success as a {roleLabel} at UAC Foods. Please read each question carefully and respond honestly. There are no trick questions.
          </p>

          <div className={styles.sectionGrid}>
            {SECTIONS.map(s => (
              <div key={s.num} className={styles.secCard}>
                <div className={styles.secHeader}>
                  <div className={styles.secNum}>{s.num}</div>
                  <div className={styles.secLabel}>Section {s.num}</div>
                </div>
                <div className={styles.secContent}>
                  <div className={styles.secTitle}>{s.title}</div>
                  <div className={styles.secDesc}>{s.desc}</div>
                  <div className={styles.secCount}>{s.count}</div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.notice}>
            <span className={styles.noticeIcon}><FiClock aria-hidden="true" /></span>
            <div className={styles.noticeContent}>
              <div className={styles.noticeTitle}>Assessment is Timed (1 hour 45 minutes)</div>
              <span className={styles.noticeText}>
                Work through each section at a steady pace. Once you submit, the assessment locks and requires administrator access to view your results. Ensure you complete the assessment in a single sitting.
              </span>
            </div>
          </div>

          <button className={styles.startBtn} onClick={handleStart}>
            Start Assessment →
          </button>
        </div>

        <div className={styles.footer}>
          © {new Date().getFullYear()} UAC Foods Limited — Proprietary Assessment Tool. Unauthorized use prohibited.
        </div>
      </div>

      {/* Pre-Assessment Modal */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Important Assessment Guidelines</h2>
              <p className={styles.modalSub}>Please read carefully before proceeding</p>
            </div>

            <div className={styles.modalContent}>
              {/* Time */}
              <div className={styles.modalSection}>
                <div className={styles.modalSectionIcon}><FiClock aria-hidden="true" /></div>
                <div>
                  <div className={styles.modalSectionTitle}>Assessment Duration</div>
                  <p className={styles.modalSectionText}>
                    <strong>Total Time: 1 Hour 45 Minutes (105 minutes)</strong> for all 4 sections combined. The timer starts when you begin and cannot be paused. Manage your time wisely across all sections.
                  </p>
                </div>
              </div>

              {/* Section Flexibility */}
              <div className={styles.modalSection}>
                <div className={styles.modalSectionIcon}><FiRefreshCw aria-hidden="true" /></div>
                <div>
                  <div className={styles.modalSectionTitle}>Section Navigation</div>
                  <p className={styles.modalSectionText}>
                    You can <strong>start with any section</strong> in any order. If one section is taking more time, you can move to another section and return later. However, all sections must be completed before time expires.
                  </p>
                </div>
              </div>

              {/* Proctoring */}
              <div className={styles.modalSection}>
                <div className={styles.modalSectionIcon}><FiVideo aria-hidden="true" /></div>
                <div>
                  <div className={styles.modalSectionTitle}>Proctored Assessment</div>
                  <p className={styles.modalSectionText}>
                    <strong>This assessment is proctored and monitored.</strong> By proceeding, you acknowledge that you understand and accept the testing conditions outlined below.
                  </p>
                </div>
              </div>

              {/* Camera Permission */}
              {requireCamera && (
                <div className={styles.modalSection}>
                  <div className={styles.modalSectionIcon}><FiCamera aria-hidden="true" /></div>
                  <div>
                    <div className={styles.modalSectionTitle}>Camera Required</div>
                    <p className={styles.modalSectionText}>
                      Your camera <strong>must be enabled</strong> for the duration of the assessment. We will use your camera to verify your identity and ensure assessment integrity.
                    </p>
                    <div style={{ marginTop: '12px' }}>
                      {cameraEnabled ? (
                        <div className={styles.cameraSuccessBox}>
                          <FiCheck aria-hidden="true" style={{ marginRight: '8px' }} />
                          <strong>Camera enabled successfully</strong>
                        </div>
                      ) : cameraError ? (
                        <div style={{ marginBottom: '8px' }}>
                          <div className={styles.cameraErrorBox}>{cameraError}</div>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className={styles.cameraRequestBtn}
                        onClick={handleRequestCamera}
                        disabled={cameraLoading || cameraEnabled || !requireCamera}
                      >
                        {cameraLoading ? 'Requesting Camera Access...' : cameraEnabled ? 'Camera Enabled' : 'Enable Camera'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!requireCamera && (
                <div className={styles.modalSection}>
                  <div className={styles.modalSectionIcon}><FiCamera aria-hidden="true" /></div>
                  <div>
                    <div className={styles.modalSectionTitle}>Camera (Optional)</div>
                    <p className={styles.modalSectionText}>
                      Camera is optional for your assessment. You can proceed without enabling your camera.
                    </p>
                  </div>
                </div>
              )}

              {/* Restrictions */}
              <div className={styles.modalSection}>
                <div className={styles.modalSectionIcon}><FiSlash aria-hidden="true" /></div>
                <div>
                  <div className={styles.modalSectionTitle}>Testing Restrictions</div>
                  <div className={styles.restrictionsList}>
                    <div className={styles.restrictionItem}>
                      <span className={styles.restrictionBullet}>●</span>
                      <span>You <strong>must remain seated</strong> for the entire duration of the assessment</span>
                    </div>
                    <div className={styles.restrictionItem}>
                      <span className={styles.restrictionBullet}>●</span>
                      <span>You <strong>cannot close this browser</strong> or minimize the assessment window</span>
                    </div>
                    <div className={styles.restrictionItem}>
                      <span className={styles.restrictionBullet}>●</span>
                      <span>You <strong>cannot navigate to another browser window</strong> or tab outside this assessment</span>
                    </div>
                    <div className={styles.restrictionItem}>
                      <span className={styles.restrictionBullet}>●</span>
                      <span>Any attempt to circumvent these rules will result in immediate disqualification</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className={styles.confirmBtn} 
                onClick={handleConfirmStart}
                disabled={requireCamera && !cameraEnabled}
                title={requireCamera && !cameraEnabled ? 'Please enable camera first' : 'Start assessment'}
              >
                I Understand, Start Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proctoring Warning Modal */}
      {showProctoringWarning && (
        <div className={styles.modalOverlay}>
          <div className={styles.proctoringWarningCard}>
            <div className={styles.proctoringHeader}>
              <div className={styles.proctoringIcon}><FiAlertTriangle aria-hidden="true" /></div>
              <h2 className={styles.proctoringTitle}>Please Be Informed Once Again</h2>
            </div>

            <div className={styles.proctoringContent}>
              <p className={styles.proctoringIntro}>
                The assessment is being <strong>monitored and proctored</strong>. Please read and confirm you understand the following conditions:
              </p>

              <div className={styles.proctoringWarnings}>
                <div className={styles.warningCard}>
                  <div className={styles.warningIcon}><FiUser aria-hidden="true" /></div>
                  <div className={styles.warningText}>
                    <strong>Be Well Seated</strong>
                    <p>Ensure you are sitting in a <strong>quiet location</strong> with <strong>no background noise</strong>. Make sure your environment is appropriate for a professional assessment.</p>
                  </div>
                </div>

                <div className={styles.warningCard}>
                  <div className={styles.warningIcon}><FiVolumeX aria-hidden="true" /></div>
                  <div className={styles.warningText}>
                    <strong>Do Not Speak</strong>
                    <p>You <strong>must not talk or communicate</strong> with anyone during the assessment. Work on the assessment <strong>silently and independently</strong>.</p>
                  </div>
                </div>

                <div className={styles.warningCard}>
                  <div className={styles.warningIcon}><FiMonitor aria-hidden="true" /></div>
                  <div className={styles.warningText}>
                    <strong>Do Not Switch Tabs</strong>
                    <p>You <strong>cannot open, switch to, or minimize</strong> any other browser tabs or windows. Stay in this assessment at all times.</p>
                  </div>
                </div>

                <div className={styles.warningCard}>
                  <div className={styles.warningIcon}><FiVideo aria-hidden="true" /></div>
                  <div className={styles.warningText}>
                    <strong>Camera Monitoring</strong>
                    <p>Your camera will <strong>continuously monitor</strong> your behavior throughout the assessment. Any suspicious activity will be flagged.</p>
                  </div>
                </div>
              </div>

              <div className={styles.proctoringAcknowledgment}>
                <strong>By clicking "I Confirm and Proceed", you acknowledge that you understand and accept all these conditions.</strong>
              </div>
            </div>

            <div className={styles.proctoringActions}>
              <button className={styles.backBtn} onClick={handleProctoringCancel}>
                ← Go Back
              </button>
              <button className={styles.proceedBtn} onClick={handleProctoringConfirm}>
                I Confirm and Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
